import React from "react";
import { formatQtyForDisplay, parseDecimalInput } from "../utils/decimalInput";
import { useTranslation } from "react-i18next";
import DateCurrentInput from "../inputs/DateCurrentInput";
import OSSignaturePanel from "./OSSignaturePanel";
import OSSubprocessItemsTable, { SubprocessItemRow, deriveFinishedReceiptSubprocessId } from "./OSSubprocessItemsTable";
import { useSubprocesses } from "../select/SubprocessCells";
import FinishedProductCard from "./FinishedProductCard";
import DetailDataCard from "./DetailDataCard";
import DetailDataSignatureRows from "./DetailDataSignatureRows";
import { Ingredient, User } from "../types/Types";

interface IngredientDetailHeaderProps {
  mode?: "os" | "rn";
  selectedItemId: string;
  selectedItemCode?: string;
  selectedItemName?: string;
  onItemChange: (value: string) => void;
  batch?: number;
  onBatchChange: (value: number) => void;
  batchUnit?: string | null;
  batchUnitCode?: string;
  waste?: number;
  onWasteChange: (value: number) => void;
  plannedWaste?: number;
  onPlannedWasteChange: (value: number) => void;
  validFrom?: string;
  onValidFromChange: (value: string) => void;
  validTo?: string;
  onValidToChange: (value: string) => void;
  selectedWorkOrderDocumentId?: string;
  onWorkOrderDocumentChange?: (value: string) => void;
  versionDisplay?: string;
  productionCode: string;
  onProductionCodeChange: (value: string) => void;
  statusFields?: { _id: string; code?: string; name: string }[];
  statusValue?: string | null;
  onStatusChange?: (id: string) => void;
  isEditable: boolean;
  showValidationErrors?: boolean;
  ingredient?: Ingredient | null;
  isCreating?: boolean;
  isSigned?: boolean;
  preparedUserId?: string;
  setPreparedUserId?: (v: string) => void;
  preparedDate?: string;
  setPreparedDate?: (v: string) => void;
  approvedUserId?: string;
  setApprovedUserId?: (v: string) => void;
  approvedDate?: string;
  setApprovedDate?: (v: string) => void;
  usersList?: User[];
  setHasChanges?: (v: boolean) => void;
  setSignField?: (v: "prepared" | "approved" | null) => void;
  onRemoveSignature?: (field: "prepared" | "approved") => void;
  subprocessItemRows: SubprocessItemRow[];
  onSubprocessItemRowsChange: (rows: SubprocessItemRow[], markDirty?: boolean) => void;
  chainSubprocessIds?: string[];
  workOrderNumber?: string;
  rnWorkOrderId?: string;
  isAdmin?: boolean;
  preparedUserName?: string;
  preparedDateDisplay?: string;
  workOrderDateDisplay?: string;
  productionPlanNumber?: string;
  plannedStartDateDisplay?: string;
  productionDateDisplay?: string;
  finishedProductLocked?: boolean;
  onResetPrepared?: () => void;
  approvedUserName?: string;
  approvedDateDisplay?: string;
  approvedSignature?: string;
  onSignApproved?: () => void;
  onRemoveApprovedSignature?: () => void;
  actionsSlot?: React.ReactNode;
  /** RN-PROST (simple layout) only: hides the Меѓу/Полупроизвод table, keeping the
   *  Готов Производ card above it. Never set on /os or the full RN screen. */
  hideSubprocessItemsTable?: boolean;
  /** RN-PROST (simple layout) only: hides the „Податоци" side card; Готов Производ takes the full width. */
  hideDetailData?: boolean;
  /** /os/archive: the archive card, rendered in the right column above Податоци. */
  archiveSlot?: React.ReactNode;
  /** Process/subprocess tabs, in their own column right after Податоци. Stays clickable when locked. */
  stageTreeSlot?: React.ReactNode;
  /** /os: approved normativ or archive — every field read-only (the stage tree slot excluded). */
  locked?: boolean;
  /** /os/archive: also block pointer events on the locked parts. */
  lockPointer?: boolean;
  /** RN operator view: subprocess ids the viewer may see. undefined = no restriction (admin / all stages). */
  allowedSubprocessIds?: Set<string>;
}

const inputCls = (editable: boolean) =>
  `h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
    editable ? "bg-white" : "bg-gray-50 cursor-not-allowed"
  }`;
const fieldCls = "flex flex-col min-w-[120px]";

const LockFieldset: React.FC<{ locked?: boolean; lockPointer?: boolean; children: React.ReactNode }> = ({ locked, lockPointer, children }) => (
  <fieldset disabled={!!locked} className={`min-w-0 w-full border-0 p-0 m-0${lockPointer ? " pointer-events-none" : ""}`}>
    {children}
  </fieldset>
);
const labelCls = "text-xs font-semibold text-gray-700 mb-1.5";

// Дозволениот отпад доаѓа од класификацијата на идентот, а кој шифрарник го носи зависи од
// книговодствената група: 5xx суровини → rawMaterialType/paType/saType; 3xx готови производи →
// tercialClass („Класификација": MED 3%, COS/BOR/HER 2%). Групите се раздвоени во податоците —
// ниту еден готов производ нема материјална класа и обратно — па tercialClass оди последен.
// Огледало на resolveAllowedWasteFromItem во server/routes/ingredients.js.
const classificationAllowedWaste = (item: any): number | null => {
  for (const c of [item?.rawMaterialType, item?.paType, item?.saType, item?.tercialClass]) {
    if (c && typeof c === "object" && c.allowedWaste != null) {
      const n = Number(c.allowedWaste);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
};

export const allowedFinishedWasteOf = (item: any): string => {
  const waste = classificationAllowedWaste(item);
  return waste == null ? "" : String(waste);
};

export const allowedFinishedYieldOf = (batch: number | null | undefined, item: any): string => {
  const qty = Number(batch);
  const waste = classificationAllowedWaste(item);
  if (!Number.isFinite(qty) || waste == null) return "";
  // Количина во ЕМ → МК децимален знак (запирка), БЕЗ групирање на илјади. Групирачка
  // запирка би се прочитала назад како децимала (истиот 10× баг опишан во decimalInput.ts).
  return formatQtyForDisplay(qty * (1 - waste / 100), { showZero: true });
};

const OSDetailHeader: React.FC<IngredientDetailHeaderProps> = ({
  mode = "os",
  selectedItemId,
  selectedItemCode,
  selectedItemName,
  onItemChange,
  batch,
  onBatchChange,
  batchUnit,
  batchUnitCode,
  waste,
  onWasteChange,
  plannedWaste,
  onPlannedWasteChange,
  validFrom,
  onValidFromChange,
  validTo,
  onValidToChange,
  selectedWorkOrderDocumentId,
  onWorkOrderDocumentChange,
  versionDisplay = "001",
  productionCode,
  onProductionCodeChange,
  statusFields,
  statusValue,
  onStatusChange,
  isEditable,
  showValidationErrors = false,
  ingredient,
  isCreating,
  isSigned,
  preparedUserId,
  setPreparedUserId,
  preparedDate,
  setPreparedDate,
  approvedUserId,
  setApprovedUserId,
  approvedDate,
  setApprovedDate,
  usersList,
  setHasChanges,
  setSignField,
  onRemoveSignature,
  subprocessItemRows,
  onSubprocessItemRowsChange,
  chainSubprocessIds,
  workOrderNumber,
  rnWorkOrderId,
  isAdmin = false,
  preparedUserName,
  preparedDateDisplay,
  workOrderDateDisplay,
  productionPlanNumber,
  plannedStartDateDisplay,
  productionDateDisplay,
  finishedProductLocked,
  onResetPrepared,
  approvedUserName,
  approvedDateDisplay,
  approvedSignature,
  onSignApproved,
  onRemoveApprovedSignature,
  actionsSlot,
  hideSubprocessItemsTable = false,
  hideDetailData = false,
  archiveSlot,
  stageTreeSlot,
  locked,
  lockPointer,
  allowedSubprocessIds,
}) => {
  const isRn = mode === "rn";
  const { t } = useTranslation();
  // Подпроцес прием на готовиот производ — издавањето на последниот ред во Меѓу/Полупроизвод.
  // Изведено од истите редови што ги црта табелата подолу, за да не се разидат.
  const subprocessOptions = useSubprocesses();
  const finishedReceiptSubprocessId = React.useMemo(() => {
    // RN appends the finished product as its own row (mapRNReceiptRows) — its Прием IS the
    // receipt subprocess. Deriving from it would read its empty Издавање, so use it directly
    // and derive only from the real Меѓу/Полупроизвод rows.
    const finishedRow = subprocessItemRows.find((row) => row.rowId?.startsWith("finished:"));
    return finishedRow?.subprocessId || deriveFinishedReceiptSubprocessId(
      subprocessItemRows.filter((row) => !row.rowId?.startsWith("finished:")),
      subprocessOptions,
      isRn ? [] : chainSubprocessIds,
      isRn
    );
  }, [subprocessItemRows, subprocessOptions, chainSubprocessIds, isRn]);
  const finishedReceiptSubprocessName = React.useMemo(
    () => subprocessOptions.find((sp) => sp._id === finishedReceiptSubprocessId)?.name || "",
    [subprocessOptions, finishedReceiptSubprocessId]
  );
  // Operator view (RN, non-admin): only the viewer's own stages are shown. undefined = no restriction.
  const restricted = isRn && allowedSubprocessIds !== undefined;
  const showFinishedCard = !restricted || allowedSubprocessIds!.has(finishedReceiptSubprocessId);
  const visibleSubprocessRows = React.useMemo(() => {
    if (!isRn) return subprocessItemRows;
    const rows = subprocessItemRows.filter((row) => !row.rowId?.startsWith("finished:"));
    return restricted ? rows.filter((row) => allowedSubprocessIds!.has(row.subprocessId)) : rows;
  }, [subprocessItemRows, isRn, restricted, allowedSubprocessIds]);
  const showItemsTable = !hideSubprocessItemsTable && (!restricted || visibleSubprocessRows.length > 0);
  const leftEmpty = restricted && !showFinishedCard && !showItemsTable;
  const masterItem = ingredient?.items && typeof ingredient.items === "object" ? ingredient.items : null;
  const finishedProductActual = subprocessItemRows.find((row) => row.rowId?.startsWith("finished:"));
  const patchFinishedProductActual = (patch: Partial<SubprocessItemRow>, markDirty = true) => {
    onSubprocessItemRowsChange(subprocessItemRows.map((row) =>
      row.rowId?.startsWith("finished:") ? { ...row, ...patch } : row
    ), markDirty);
  };

  return (
    <div className="w-full mt-4 relative">
      <div className="hidden">

        {/* Valid From */}
        <div className={`${fieldCls} w-32`}>
          <label className={labelCls}>{t("tableHeaders.validFrom") || "Valid From"}</label>
          <div className="h-10 [&_.react-datepicker-wrapper]:w-full [&_input]:w-full [&_input]:h-10 [&_input]:px-3 [&_input]:text-sm [&_input]:border [&_input]:border-gray-300 [&_input]:rounded-lg [&_input]:outline-none [&_input]:focus:ring-2 [&_input]:focus:ring-blue-500">
            <DateCurrentInput value={validFrom || ""} onChange={onValidFromChange} disabled={!isEditable} />
          </div>
        </div>

        {/* Valid To */}
        <div className={`${fieldCls} w-32`}>
          <label className={labelCls}>{t("tableHeaders.validTo") || "Valid To"}</label>
          <div className="h-10 [&_.react-datepicker-wrapper]:w-full [&_input]:w-full [&_input]:h-10 [&_input]:px-3 [&_input]:text-sm [&_input]:border [&_input]:border-gray-300 [&_input]:rounded-lg [&_input]:outline-none [&_input]:focus:ring-2 [&_input]:focus:ring-blue-500">
            <DateCurrentInput value={validTo || ""} onChange={onValidToChange} disabled={!isEditable} />
          </div>
        </div>

        {/* Шифра на ПП (Production code) */}
        <div className={`${fieldCls} w-24`}>
          <label className={labelCls}>{t("tableHeaders.productionCode", "Шифра на ПП")}</label>
          {/* type="text": free-form alphanumeric code (e.g. "RD MP 37"), not numeric */}
          <input
            type="text"
            value={productionCode}
            onChange={(e) => onProductionCodeChange(e.target.value)}
            disabled={!isEditable}
            className={inputCls(isEditable)}
          />
        </div>

        {/* Version */}
        <div className={`${fieldCls} w-14`}>
          <label className={labelCls}>{t("tableHeaders.version") || "Ver"}</label>
          <div className="h-10 px-3 border border-gray-300 rounded-lg bg-gray-50 text-sm flex items-center justify-center text-gray-700 font-semibold">
            {versionDisplay}
          </div>
        </div>

      </div>

      {/* Row 2 — full-width proportional layout */}
      <div className="grid w-full grid-cols-12 gap-x-3 items-start">

        {/* Меѓу/Полупроизвод */}
        {!leftEmpty && <div className={`${hideDetailData ? "col-span-12" : stageTreeSlot ? "col-span-8" : "col-span-9"} min-w-0`}>
          <LockFieldset locked={locked} lockPointer={lockPointer}>
          {showFinishedCard && <FinishedProductCard
            mode={mode}
            selectedItemId={selectedItemId}
            onItemChange={onItemChange}
            batch={batch}
            onBatchChange={onBatchChange}
            batchUnit={batchUnit}
            batchUnitCode={batchUnitCode}
            isEditable={isEditable && !restricted}
            showValidationErrors={showValidationErrors}
            masterItem={masterItem}
            selectedWorkOrderDocumentId={selectedWorkOrderDocumentId}
            onWorkOrderDocumentChange={onWorkOrderDocumentChange}
            selectedItemCode={selectedItemCode}
            selectedItemName={selectedItemName}
            workOrderNumber={workOrderNumber}
            workOrderDate={workOrderDateDisplay}
            productionPlanNumber={productionPlanNumber}
            plannedStartDateDisplay={plannedStartDateDisplay}
            productionDateDisplay={productionDateDisplay}
            serialNumber={finishedProductActual?.serialNumber}
            expiryDate={finishedProductActual?.expiryDate}
            onFinishedProductChange={patchFinishedProductActual}
            finishedProductLocked={finishedProductLocked}
            receiptSubprocessName={finishedReceiptSubprocessName}
            receivedQty={finishedProductActual?.measuredQty}
            receivedQtyConfirmed={finishedProductActual?.confirmed}
            receivedQtyEditable={isRn && hideSubprocessItemsTable}
            onReceivedQtyChange={(value) => patchFinishedProductActual({
              measuredQty: value,
              confirmed: (parseDecimalInput(value) ?? 0) > 0,
            })}
          />}
          {showItemsTable && (
            <OSSubprocessItemsTable
              // The finished-product helper row is already shown in Готов Производ above.
              // Safe to drop from display only: in RN this table is readOnly and never emits onChange.
              rows={visibleSubprocessRows}
              onChange={onSubprocessItemRowsChange}
              chainSubprocessIds={isRn ? undefined : chainSubprocessIds}
              readOnly={isRn}
            />
          )}
          </LockFieldset>
        </div>}

        {!hideDetailData && <div className={`${stageTreeSlot && !leftEmpty ? "col-span-2" : "col-span-3"} min-w-0`}>
          <LockFieldset locked={locked} lockPointer={lockPointer}>
          {archiveSlot}
          <DetailDataCard
            mode={mode}
            isEditable={isEditable}
            productionCode={productionCode}
            onProductionCodeChange={onProductionCodeChange}
            statusFields={statusFields}
            statusValue={statusValue}
            onStatusChange={onStatusChange}
            versionDisplay={versionDisplay}
            validFrom={validFrom}
            onValidFromChange={onValidFromChange}
            validTo={validTo}
            onValidToChange={onValidToChange}
            rnWorkOrderId={rnWorkOrderId}
            rnWorkOrderNumber={workOrderNumber}
            actionsSlot={actionsSlot}
            operatorView={restricted}
            productLabel={[selectedItemCode, selectedItemName].filter(Boolean).join(" — ")}
            batchLabel={finishedProductActual?.serialNumber}
            dateLabel={productionDateDisplay || workOrderDateDisplay}
            signature={
              isRn ? (
                <DetailDataSignatureRows
                  mode="rn"
                  isCreating={!!isCreating}
                  isAdmin={isAdmin}
                  preparedUserName={preparedUserName}
                  preparedDate={preparedDateDisplay}
                  onResetPrepared={onResetPrepared}
                  approvedUserName={approvedUserName}
                  approvedDate={approvedDateDisplay}
                  approvedSignature={approvedSignature}
                  setSignField={(field) => { if (field === "approved") onSignApproved?.(); }}
                  onRemoveSignature={(field) => { if (field === "approved") onRemoveApprovedSignature?.(); }}
                />
              ) : (
                <DetailDataSignatureRows
                  ingredient={ingredient || null}
                  isCreating={!!isCreating}
                  isSigned={!!isSigned}
                  preparedUserId={preparedUserId || ""}
                  setPreparedUserId={setPreparedUserId || (() => {})}
                  preparedDate={preparedDate || ""}
                  setPreparedDate={setPreparedDate || (() => {})}
                  approvedUserId={approvedUserId || ""}
                  setApprovedUserId={setApprovedUserId || (() => {})}
                  approvedDate={approvedDate || ""}
                  setApprovedDate={setApprovedDate || (() => {})}
                  usersList={usersList || []}
                  setHasChanges={setHasChanges || (() => {})}
                  setSignField={setSignField || (() => {})}
                  onRemoveSignature={onRemoveSignature || (() => {})}
                />
              )
            }
          />
          </LockFieldset>
        </div>}

        {/* Процесите: своја колона веднаш десно од Податоци, вертикално. */}
        {!hideDetailData && stageTreeSlot && <div className={`${leftEmpty ? "col-span-3" : "col-span-2"} min-w-0`}>{stageTreeSlot}</div>}

        {/* Status */}
        <div className="hidden" />

        {/* Description */}
        <div className="hidden" />

        {/* Prepared / Approved — full 3-line signature blocks */}
        {!isRn && (
        <div className="hidden">
          <OSSignaturePanel
            ingredient={ingredient || null}
            isCreating={!!isCreating}
            isSigned={!!isSigned}
            preparedUserId={preparedUserId || ""}
            setPreparedUserId={setPreparedUserId || (() => {})}
            preparedDate={preparedDate || ""}
            setPreparedDate={setPreparedDate || (() => {})}
            approvedUserId={approvedUserId || ""}
            setApprovedUserId={setApprovedUserId || (() => {})}
            approvedDate={approvedDate || ""}
            setApprovedDate={setApprovedDate || (() => {})}
            usersList={usersList || []}
            setHasChanges={setHasChanges || (() => {})}
            setSignField={setSignField || (() => {})}
            onRemoveSignature={onRemoveSignature || (() => {})}
          />
        </div>
        )}

      {/* Подпроцеси — per-ident subprocess checkboxes (filter the OS stage tree) */}
      <div className="hidden">
        <div className="flex flex-col">
          <label className={labelCls}>{t("sidebar.subprocesses", "Подпроцеси")}</label>
        </div>
      </div>

      </div>
    </div>
  );
};

export default OSDetailHeader;
